import { all, call, put, select } from 'redux-saga/effects';

import { getPackages as uGetPackages, getSpecialOffers as uGetSpecialOffers } from 'utils/fonts';
import { generateTakeLatestSaga } from 'utils/sagas';
import request from 'utils/request';
import { get, join, map } from 'lodash';

import {
	DOWNLOAD_FONT_PACKAGE,
	GET_SPECIAL_OFFERS,
	LOAD_CHARACTERS,
	LOAD_GALLERY,
	LOAD_HOW_TO_USE,
	LOAD_ONELINERS,
	LOAD_PACKAGES,
	LOAD_PRODUCT_STYLES,
	LOAD_PRODUCTS,
	LOAD_SUGGESTED_PAIRINGS,
	LOAD_TRY_LAYOUT
} from './constants';

import {
	charactersLoaded,
	charactersLoadingError,
	downloadFontPackageError,
	fontPackageDownloaded,
	galleryLoaded,
	galleryLoadingError,
	getPackageStylesError,
	getPackageStylesSuccess,
	howToUseLoaded,
	howToUseLoadingError,
	oneLinersLoaded,
	oneLinersLoadingError,
	packagesLoaded,
	packagesLoadingError,
	productsLoaded,
	productsLoadingError,
	productStylesLoaded,
	productStylesLoadingError,
	saveResizeSettings,
	specialOffersLoaded,
	specialOffersLoadingError,
	suggestedPairingsLoaded,
	suggestedPairingsLoadingError,
	tryLayoutLoaded,
	tryLayoutLoadingError
} from './actions';
import { getPrices } from 'pages/App/actions';

import { selectProductLine } from './selectors';
import { selectLicenseState } from 'store/licenses/selectors';

/**
 * Handlers
 */

// Products request/response handler
export function* getProducts() {
	const url = '/api/v1/product_lines?include=styles';
	try {
		const response = yield call(request, url);
		yield put(productsLoaded(response.data));
	} catch (err) {
		yield put(productsLoadingError(err.message));
	}
}

// Product styles request/response handler
export function* getProductStyles() {
	const productLine = yield select(selectProductLine());
	const url = `/api/v1/product_lines/${productLine.id}?include=multipurpose_styles,office_styles,screen_smart_styles`;

	try {
		const response = yield call(request, url);
		yield put(productStylesLoaded(response.data));
	} catch (err) {
		yield put(productStylesLoadingError(err.message));
	}
}

// Load the Try layout associated with the active product line
export function* getTryLayout() {
	const productLine = yield select(selectProductLine());

	const desktopCode = get(productLine, 'attributes.desktop_try_code', false);
	const mobileCode = get(productLine, 'attributes.mobile_try_code', false);

	if (!desktopCode || !mobileCode) {
		yield put(tryLayoutLoadingError('Missing Overview layout.'));
	} else {
		try {
			const [desktop, mobile] = yield all([
				call(request, `/api/v1/try/layout/${desktopCode}`),
				call(request, `/api/v1/try/layout/${mobileCode}`)
			]);

			yield put(tryLayoutLoaded({
				desktop: get(desktop, 'data.data.attributes.layout_data'),
				mobile: get(mobile, 'data.data.attributes.layout_data')
			}));
		} catch (err) {
			yield put(tryLayoutLoadingError(err.message));
		}
	}
}

// Packages request/response handler
export function* getPackages() {
	// Select product line from store
	const productLine = yield select(selectProductLine());
	const licenses = yield select(selectLicenseState());

	if (productLine) {
		try {
			const response = yield call(uGetPackages, productLine.id, licenses.computer.value, licenses.selfHosted.value);
			const packages = get(response, 'data.items.productline_items');
			const relatedPackages = get(response, 'data.related_packages.related_packages');
			const packageIDs = join(map(packages, 'id'));
			const resizeSettings = get(response, 'data.style_layouts.style_layouts');

			yield all([
				put(saveResizeSettings(resizeSettings)),
				put(getPrices(packageIDs)),
				put(packagesLoaded({ packages, relatedPackages })),
				put(getPackageStylesSuccess(response.data))
			]);
		} catch (err) {
			yield all([
				put(packagesLoadingError(err.message)),
				put(getPackageStylesError(err.message))
			]);
		}
	}
}

// Packages request/response handler
export function* getCharacters(productLine) {
	const url = `/api/v1/product_lines/${productLine.id}?include=character_sets`;

	try {
		const response = yield call(request, url);
		yield put(charactersLoaded(response.data));
	} catch (err) {
		yield put(charactersLoadingError(err.message));
	}
}

// Special offers request/response handler
export function* getSpecialOffers() {
	// Select product line from store
	const productLine = yield select(selectProductLine('From packageStyles()'));
	const theLicenses = yield select(selectLicenseState());

	if (productLine) {
		try {
			const response = yield call(uGetSpecialOffers, productLine.id, theLicenses.computer.value || 1);
			yield put(specialOffersLoaded(response.data));
		} catch (err) {
			yield put(specialOffersLoadingError(err));
		}
	}
}

// Gallery request/response handler
export function* getGallery() {
	// Select product line from store
	const productLine = yield select(selectProductLine());

	if (productLine) {
		const url = `/api/v1/cms/gallery/product_line/${productLine.id}?include=related_entries&serializer=array`;
		try {
			const response = yield call(request, url);
			yield put(galleryLoaded(response.data));
		} catch (err) {
			yield put(galleryLoadingError(err.message));
		}
	}
}

// How 2 Use request/response handler
export function* getHowToUse() {
	// Select product line from store
	const productLine = yield select(selectProductLine());
	if (productLine) {
		const url = `/api/v1/cms/how_to_use/product_line/${productLine.id}?serializer=array`;
		try {
			const response = yield call(request, url);
			yield put(howToUseLoaded(response.data));
		} catch (err) {
			yield put(howToUseLoadingError(err.message));
		}
	}
}

// One-liner request/response handler
export function* getOneLiners() {
	// Select product line from store
	const productLine = yield select(selectProductLine());
	if (productLine) {
		// This is a temporary condition, until all SVGs are generated for all products
		const url = `/api/v1/product_lines/${productLine.id}?include=multipurpose_styles,office_styles,screen_smart_styles`;

		try {
			const response = yield call(request, url);
			yield put(oneLinersLoaded(response.data));
		} catch (err) {
			yield put(oneLinersLoadingError(err.message));
		}
	}
}

// Suggested Pairings
export function* getSuggestedPairings() {
	const productLine = yield select(selectProductLine());
	const url = `/api/v1/product_lines/${productLine.id}?include=suggestions&serializer=array`;
	try {
		const response = yield call(request, url);
		yield put(suggestedPairingsLoaded(response.data.suggestions.suggestions));
	} catch (err) {
		yield put(suggestedPairingsLoadingError(err.message));
	}
}

export function* downloadFontPackage(action) {
	const url = action.data.downloadURI;
	try {
		let apiUrl = `/api${url}`;
		if (action.data.isMobile !== undefined) {
			apiUrl = `${apiUrl}?isMobile=${action.data.isMobile}`;
		}
		const response = yield call(request, apiUrl);
		location.href = response.data.download_uri;
		if (action.options && action.options.success) {
			yield call(action.options.success);
		}
		yield put(fontPackageDownloaded());
	} catch (err) {
		yield put(downloadFontPackageError(err.message));
	}
}

/* _____________________________________________________________________________
 * Root saga watchers
 *
 * Since we're code-splitting on routes and load the saga with each route
 * change, we need to ensure that the sagas aren't run more than once.
 * ___________________________________________________________________________ */

export const getProductsSaga = generateTakeLatestSaga(LOAD_PRODUCTS, getProducts);
export const getCharactersSaga = generateTakeLatestSaga(LOAD_CHARACTERS, getCharacters);
export const getGallerySaga = generateTakeLatestSaga(LOAD_GALLERY, getGallery);
export const getHowToUseSaga = generateTakeLatestSaga(LOAD_HOW_TO_USE, getHowToUse);
export const getPackagesSaga = generateTakeLatestSaga(LOAD_PACKAGES, getPackages);
export const getSpecialOffersSaga = generateTakeLatestSaga(GET_SPECIAL_OFFERS, getSpecialOffers);
export const getOneLinersSaga = generateTakeLatestSaga(LOAD_ONELINERS, getOneLiners);
export const getSuggestedPairingsSaga = generateTakeLatestSaga(LOAD_SUGGESTED_PAIRINGS, getSuggestedPairings);
export const downloadFontPackageSaga = generateTakeLatestSaga(DOWNLOAD_FONT_PACKAGE, downloadFontPackage);
export const getProductStylesSaga = generateTakeLatestSaga(LOAD_PRODUCT_STYLES, getProductStyles);
export const getTryLayoutSaga = generateTakeLatestSaga(LOAD_TRY_LAYOUT, getTryLayout);

// Bootstrap sagas
export default [
	downloadFontPackageSaga,
	getCharactersSaga,
	getGallerySaga,
	getHowToUseSaga,
	getOneLinersSaga,
	getPackagesSaga,
	getProductsSaga,
	getProductStylesSaga,
	getSpecialOffersSaga,
	getSuggestedPairingsSaga,
	getTryLayoutSaga
];
